// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Layout.tsx
 * @input Uses React, stack/stackItem utilities, LayoutAreaContext, LayoutSlotsContext
 * @output Exports Layout component and LayoutProps, LayoutHeight types
 * @position Page shell and app layout — use for any page with a header, sidebar, or content area.
 *   Building a page with a sidebar? Use Layout with start/end slots.
 *   Need a header + scrollable content? Use Layout with header + content slots.
 *   Manages padding collapse, scroll containment, and responsive slot sizing automatically.
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/Layout/Layout.doc.mjs
 * - /apps/storybook/stories/Layout.stories.tsx
 * - /packages/cli/assets/templates/blocks/components/Layout/ (showcase blocks)
 */
import { useMemo } from 'react';
import * as stylex from '@stylexjs/stylex';
import { LayoutAreaContext } from "./LayoutAreaContext.js";
import { LayoutSlotsContext } from "./LayoutSlotsContext.js";
import { LayoutDividerContext } from "./LayoutDividerContext.js";
import { stack } from "../Stack/stack.stylex.js";
import { stackItem } from "../Stack/stackItem.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { layoutPaddingOuterXVarStyles, layoutPaddingOuterYVarStyles } from "./padding.stylex.js";

/**
 * Height behavior for the layout.
 * - `fill`: Layout fills container height, content scrolls internally (default)
 * - `auto`: Layout grows with content, container/page scrolls
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  layoutOuter: {
    keTefX: "xojxgvx",
    k71WvV: "x1fcf3bl",
    keoZOQ: "x1sa9bsh",
    k1K539: "x6h7pi7",
    $$css: true
  },
  layoutInner: {
    "--container-padding-inline-start": "xrhngw9",
    "--container-padding-inline-end": "xjsfl84",
    "--container-padding-block-start": "x1047aw6",
    "--container-padding-block-end": "xax9j7h",
    $$css: true
  },
  fill: {
    kZKoxP: "x12qplqi",
    kskxy: "xenllk4",
    $$css: true
  },
  auto: {
    kAzted: "x1us19tq",
    $$css: true
  },
  middle: {
    kUk6DE: "x98rzlu",
    kAzted: "x2lwn1j",
    $$css: true
  },
  fullBleed: {
    "--layout-padding-outer-x": "x1wbjvqu",
    "--layout-padding-outer-y": "xzxxx64",
    $$css: true
  }
};
const _temp = {
  kzqmXN: "xh8yej3",
  kUOVxO: "xvueqy4",
  "$$css": true
};
const dynamicStyles = {
  contentWidthVar: width => [{
    "--layout-content-width": (typeof width === 'number' ? `${width}px` : width) != null ? "x4906uf" : typeof width === 'number' ? `${width}px` : width,
    $$css: true
  }, {
    "--x---layout-content-width": (typeof width === 'number' ? `${width}px` : width) != null ? typeof width === 'number' ? `${width}px` : width : undefined
  }],
  contentWidth: width => [_temp, {
    ks0D6T: (typeof width === 'number' ? `${width}px` : width) != null ? "xf68679" : typeof width === 'number' ? `${width}px` : width,
    $$css: true
  }, {
    "--x-maxWidth": (val => typeof val === "number" ? val + "px" : val != null ? val : undefined)(typeof width === 'number' ? `${width}px` : width)
  }]
};
/**
 * Helper component to wrap content in layout area context.
 */
function AreaProvider({
  area,
  children
}) {
  if (children == null) {
    return null;
  }
  return /*#__PURE__*/_jsx(LayoutAreaContext, {
    value: area,
    children: children
  });
}

/**
 * Page shell with header, sidebar(s), content, and footer slots.
 * Use this for full-page layouts, app shells, dashboard layouts, or any UI
 * that needs a header bar, side navigation, scrollable content area, or action footer.
 * Can be used standalone for page-level layouts, or inside a container
 * (Card, Section) for content-level layouts.
 *
 * Handles padding collapse between adjacent slots, scroll containment in the
 * content area, and automatic RTL support via CSS logical properties.
 *
 * Structure:
 * ```
 * ┌─────────────────────────────────────────┐
 * │                 header                  │
 * ├──────┬─────────────────────────┬────────┤
 * │      │                         │        │
 * │start │        content          │  end   │
 * │      │                         │        │
 * ├──────┴─────────────────────────┴────────┤
 * │                 footer                  │
 * └─────────────────────────────────────────┘
 * ```
 *
 * When to use Layout vs raw flexbox:
 * - Page with a sidebar → Layout with `start` slot
 * - Dashboard with header + scrollable body → Layout with `header` + `content`
 * - Settings page with nav panel → Layout with `start` + `content`
 * - Simple vertical stack of items → use VStack instead
 *
 * @example
 * ```
 * <Layout
 *   header={<LayoutHeader hasDivider>App Name</LayoutHeader>}
 *   start={
 *     <LayoutPanel hasDivider width={240} role="navigation">
 *       <Navigation />
 *     </LayoutPanel>
 *   }
 *   content={
 *     <LayoutContent role="main">
 *       <MainContent />
 *     </LayoutContent>
 *   }
 * />
 * ```
 */
const _temp2 = {
  "x-default-marker": "x-default-marker",
  $$css: true
};
export function Layout({
  children,
  content,
  contentWidth,
  defaultHasDividers,
  end,
  footer,
  header,
  height = 'fill',
  padding,
  ref,
  start,
  xstyle,
  className,
  style
}) {
  const isFill = height === 'fill';
  // Children are a shorthand for the content slot; an explicit `content` prop
  // wins when both are provided.
  const resolvedContent = content ?? children;
  const dividerCtxValue = useMemo(() => defaultHasDividers != null ? {
    defaultHasDividers
  } : null, [defaultHasDividers]);

  // Memoize slots info to avoid unnecessary re-renders
  const hasHeader = header != null;
  const hasFooter = footer != null;
  const hasStart = start != null;
  const hasEnd = end != null;
  const slotsValue = useMemo(() => ({
    hasHeader,
    hasFooter,
    hasStart,
    hasEnd
  }), [hasHeader, hasFooter, hasStart, hasEnd]);
  const tree = /*#__PURE__*/_jsx(LayoutSlotsContext, {
    value: slotsValue,
    children: /*#__PURE__*/_jsx("div", {
      ref: ref,
      ...mergeProps(themeProps('layout', {
        height
      }), stylex.props(styles.layoutOuter, isFill ? styles.fill : styles.auto, xstyle), className, style),
      children: /*#__PURE__*/_jsxs("div", {
        ...stylex.props(_temp2, styles.layoutInner, ...stack({
          direction: 'vertical'
        }), isFill ? styles.fill : styles.auto, padding === 0 && styles.fullBleed, padding != null && layoutPaddingOuterXVarStyles[padding], padding != null && layoutPaddingOuterYVarStyles[padding], contentWidth != null && dynamicStyles.contentWidthVar(contentWidth)),
        children: [/*#__PURE__*/_jsx(AreaProvider, {
          area: "header",
          children: header
        }), /*#__PURE__*/_jsxs("div", {
          ...stylex.props(...stack({
            direction: 'horizontal'
          }), styles.middle, contentWidth != null && dynamicStyles.contentWidth(contentWidth)),
          children: [/*#__PURE__*/_jsx(AreaProvider, {
            area: "start",
            children: start
          }), /*#__PURE__*/_jsx("div", {
            ...stylex.props(...stackItem({
              size: 'fill'
            })),
            children: /*#__PURE__*/_jsx(AreaProvider, {
              area: "content",
              children: resolvedContent
            })
          }), /*#__PURE__*/_jsx(AreaProvider, {
            area: "end",
            children: end
          })]
        }), /*#__PURE__*/_jsx(AreaProvider, {
          area: "footer",
          children: footer
        })]
      })
    })
  });
  if (dividerCtxValue != null) {
    return /*#__PURE__*/_jsx(LayoutDividerContext, {
      value: dividerCtxValue,
      children: tree
    });
  }
  return tree;
}
Layout.displayName = 'Layout';